import {
  createFormatterDataModel,
  ICustomNumberFormatConfig,
  IFormatterDataModel,
} from '../../data-models/formatter.data-model';
import { PpsDefaultDecimals } from '../../schemas/common-schema-defs';
import { EM_DASH, NOT_MEANINGFUL } from './EntityManagementFormatters';
import { FMT } from '../formatter-service';

const DefaultCurrency = 'USD';

export enum NumericFormatterId {
  currency = 'currency',
  currency0Dp = 'currency0Dp',
  currencyShort = 'currencyShort',
  currencyShort1dp = 'currencyShort1dp',
  currencyShort2dp = 'currencyShort2dp',
  currencyShortMax1dp = 'currencyShortMax1dp',
  decimalPercent = 'decimalPercent',
  entityInteger = 'entityInteger',
  entityMultiple = 'entityMultiple',
  entityPercent = 'entityPercent',
  integer = 'integer',
  multiplier = 'multiplier',
  multiplierHighPrecision = 'multiplierHighPrecision',
  naturalNumber = 'naturalNumber',
  numeric = 'numeric',
  numeric1DP = 'numeric1DP',
  numeric2DP = 'numeric2DP',
  numericShort = 'numericShort',
  percent = 'percent',
  percent0Dp = 'percent0Dp',
  percent2DpShort = 'percent2DpShort',
  percentHighPrecision = 'percentHighPrecision',
  usd = 'usd',
  usdShort = 'usdShort',
}

export const NumericFormattersConfig: Record<
  NumericFormatterId,
  IFormatterDataModel<ICustomNumberFormatConfig>
> = {
  currency: {
    id: 'currency',
    type: 'number',
    config: {
      style: 'currency',
      currency: DefaultCurrency,
      maximumFractionDigits: 2,
    },
  },
  currency0Dp: {
    id: 'currency0Dp',
    type: 'number',
    config: {
      currency: 'USD',
      style: 'currency',
      maximumFractionDigits: 0,
    },
  },
  currencyShort: {
    id: 'currencyShort',
    type: 'number',
    config: {
      compactDisplay: 'short',
      currency: 'USD',
      notation: 'compact',
      style: 'currency',
    },
  },
  currencyShort1dp: {
    id: 'currencyShort1dp',
    type: 'number',
    config: {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      compactDisplay: 'short',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    },
  },
  // $42.8M and $808K side by side, as the Allocation Model design shows them.
  currencyShortMax1dp: {
    id: 'currencyShortMax1dp',
    type: 'number',
    config: {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1,
    },
  },
  currencyShort2dp: {
    id: 'currencyShort2dp',
    type: 'number',
    config: {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      compactDisplay: 'short',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      // 'expand' rounds away from zero: ceil for positives, floor for negatives.
      // This matches the intent "displayed value is never less than the raw" for both signs.
      roundingMode: 'expand',
    },
  },
  decimalPercent: {
    id: 'decimalPercent',
    type: 'number',
    config: {
      style: 'percent',
      maximumFractionDigits: 2,
    },
  },
  /**
   * Entity Management multiple display (FD-4464, spec AC 5/8). `multiplier` with the
   * module's semantics layered on: fixed one decimal, `n/m` for computed-but-meaningless
   * results (negative multiples on GP/carry vehicles, NaN/Infinity), and an em dash for
   * absent values so "no multiple" stays distinguishable from a real 0.0x.
   * Config is self-contained (suffix included) because init-time registration does not
   * merge `extends` — only the lazy field-model path does.
   */
  entityMultiple: {
    id: 'entityMultiple',
    type: 'number',
    extends: 'multiplier',
    config: {
      suffix: 'x',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
      noValue: EM_DASH,
      invalidValue: NOT_MEANINGFUL,
      negativeValue: NOT_MEANINGFUL,
    },
  },
  /**
   * Entity Management's percentage and count, which differ from `percent` and `integer`
   * in one respect: a missing value renders as an em dash rather than an empty string.
   *
   * That distinction is the module's whole display convention (spec §4.5) — an absent
   * figure has to be visibly absent, because a blank cell reads as a rendering bug and a
   * zero would be a false claim. `entityCurrency` and `entityMultiple` already do this;
   * these two complete the set for the company detail panel's counts and rates.
   *
   * Self-contained rather than `extends`, matching `entityMultiple`: init-time
   * registration does not merge an `extends` chain, so the suffix and precision are
   * repeated here on purpose.
   */
  entityPercent: {
    id: 'entityPercent',
    type: 'number',
    config: {
      suffix: '%',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      noValue: EM_DASH,
    },
  },
  entityInteger: {
    id: 'entityInteger',
    type: 'number',
    config: {
      maximumFractionDigits: 0,
      noValue: EM_DASH,
    },
  },
  integer: {
    id: 'integer',
    type: 'number',
    config: {
      maximumFractionDigits: 0,
    },
  },
  multiplier: {
    id: `multiplier`,
    type: 'number',
    extends: 'numeric',
    config: {
      suffix: 'x',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    },
  },
  multiplierHighPrecision: {
    id: `multiplierHighPrecision`,
    type: 'number',
    config: {
      suffix: 'x',
      minimumFractionDigits: 0,
      maximumFractionDigits: 8,
    },
  },
  naturalNumber: {
    id: 'naturalNumber',
    type: 'number',
    config: {
      maximumFractionDigits: 0,
      negativeValue: '0',
    },
  },
  numeric: {
    id: 'numeric',
    type: 'number',
    config: {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
      signDisplay: 'negative',
    },
  },
  numeric1DP: {
    id: 'numeric1DP',
    type: 'number',
    config: {
      maximumFractionDigits: 1,
      minimumFractionDigits: 1,
    },
  },
  numeric2DP: {
    id: 'numeric2DP',
    type: 'number',
    config: {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    },
  },
  numericShort: {
    id: 'numericShort',
    type: 'number',
    config: {
      compactDisplay: 'short',
      notation: 'compact',
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    },
  },
  percent: {
    id: 'percent',
    type: 'number',
    config: {
      suffix: '%',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
  percent0Dp: {
    id: 'percent0Dp',
    type: 'number',
    config: {
      suffix: '%',
      maximumFractionDigits: 0,
    },
  },
  percentHighPrecision: {
    id: 'percentHighPrecision',
    type: 'number',
    config: {
      suffix: '%',
      minimumFractionDigits: 0,
      maximumFractionDigits: 8,
    },
  },
  percent2DpShort: {
    id: 'percent2DpShort',
    type: 'number',
    config: {
      suffix: '%',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      compactDisplay: 'short',
      notation: 'compact',
    },
  },
  usd: {
    id: 'usd',
    type: 'number',
    config: {
      currency: 'USD',
      style: 'currency',
    },
  },
  usdShort: {
    id: 'usdShort',
    type: 'number',
    config: {
      compactDisplay: 'short',
      currency: 'USD',
      notation: 'compact',
      style: 'currency',
    },
  },
};

export const usdLong2DPModel: IFormatterDataModel<ICustomNumberFormatConfig> = {
  type: 'number',
  id: `USD-long`,
  config: {
    currency: 'USD',
    style: 'currency',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  },
};

const _formatUSDLong2dp = (value: number) => FMT.format(usdLong2DPModel, value);

export function formatUSDShort(amount: number) {
  if (amount > -1 && amount < 0.5) {
    return '$0';
  } else if (amount >= 0.5 && amount < 1) {
    return '$1';
  }
  return FMT.format('currencyShort', amount);
}

function createFormatter(id: keyof typeof NumericFormatterId): (value: number) => string {
  return (value: number) => FMT.format(id, value);
}

/** @deprecated use {@link FormatterService.format} directly instead. */
export const formatNumber2dp = createFormatter('numeric');

/** @deprecated use {@link FormatterService.format} directly instead. */
export const formatNumberToFixed2dp = createFormatter('numeric2DP');

/** @deprecated use {@link FormatterService.format} directly instead. */
export const formatInteger = createFormatter('integer');
const _formatUSD = createFormatter('usd');

/** @deprecated use {@link FormatterService.format} directly instead. */
export const formatPercent2dpAsIs = (value: number) => {
  return `${formatNumber2dp(value)}%`;
};

export const formatUSDLong2dp = (value?: number | null): string => {
  if (value != null) {
    return _formatUSDLong2dp(value);
  }
  return '';
};

export const formatUSD = (value?: number | null) => {
  if (value != null) {
    return _formatUSD(value);
  }
  return '';
};

export const formatUSDRound = (value?: number | null) => {
  if (value != null) {
    return FMT.format('currency0Dp', value);
  }
  return '';
};

export function adjustValueWithAccountingErrorMargin(value: number): number {
  if (value > -1 && value < 0.5) {
    return 0;
  } else if (value >= 0.5 && value < 1) {
    return 1;
  }
  return value;
}

// Abbreviated 2dp currency for figures shown side by side, where a differing number of
// decimals between neighbours reads as a glitch. Deliberately its own id rather than
// `currencyShort2dp`: tenant formatter configs are registered over same-id code
// formatters at boot, and a config that relaxes minimumFractionDigits would reintroduce
// the mismatch this exists to prevent.
export const CurrencyCompact2dpFormatterDataModel: IFormatterDataModel<ICustomNumberFormatConfig> =
  createFormatterDataModel({
    id: 'currency-compact-2dp',
    type: 'number',
    config: {
      currency: 'USD',
      style: 'currency',
      notation: 'compact',
      compactDisplay: 'short',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      // Matches currencyShort2dp: never display less than the raw value.
      roundingMode: 'expand',
    },
  });

export const PpsFormatterDataModel: IFormatterDataModel<ICustomNumberFormatConfig> = createFormatterDataModel(
  {
    id: 'pps-6',
    type: 'number',
    config: {
      currency: 'USD',
      style: 'currency',
      maximumFractionDigits: PpsDefaultDecimals,
      minimumFractionDigits: PpsDefaultDecimals,
    },
  }
);

// 2-decimal price-per-share. A PPS is legitimately sub-dollar, so we opt out of the
// accounting-error margin that the default 2dp `currency` formatter applies (it rounds
// values in (-1, 0.5) to 0, e.g. $0.4907 → $0.00).
export const Pps2dpFormatterDataModel: IFormatterDataModel<ICustomNumberFormatConfig> =
  createFormatterDataModel({
    id: 'pps-2',
    type: 'number',
    config: {
      currency: 'USD',
      style: 'currency',
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
      adjustWithAccountingErrorMargin: false,
    },
  });

// Up to 2dp with no accounting margin, so a $0.40 price stays $0.40. Not a `currency*` id, which the app-wide
// currency toggle would rewrite: callers show it in the row's own currency with modelInCurrency.
export const Amount2dpFormatterDataModel: IFormatterDataModel<ICustomNumberFormatConfig> =
  createFormatterDataModel({
    id: 'amount-2dp',
    type: 'number',
    config: {
      currency: 'USD',
      style: 'currency',
      maximumFractionDigits: 2,
      adjustWithAccountingErrorMargin: false,
    },
  });
